import { useTranslation } from 'react-i18next'

import { useMediaQuery } from '#/shared/lib/hooks'
import {
  openPhoneKeypad,
  usePhonePanelViewStore,
} from '#/shared/lib/phone-panel'
import { cn } from '#/shared/lib/utils'
import {
  BellOffIcon,
  BellRingIcon,
  KeypadIcon,
  MessageIcon,
  MicIcon,
  PauseIcon,
  SpeakerIcon,
  TransferIcon,
} from '#/shared/ui/icons'

import type { CallMainline, CallMatch } from '../model/types'
import { CALL_PHASE } from '../model/use-active-call'
import type { CallPhase } from '../model/use-active-call'
import { ANSWER_COST, useAnswerCost } from '../model/use-answered-calls'
import { MainlineBadge } from './mainline-badge'
import { MatchList } from './match-list'
import { PillTranscript } from './pill-transcript'

type IconComponent = React.ComponentType<{ className?: string }>

const CONTROL_ID = {
  mute: 'mute',
  speaker: 'speaker',
  hold: 'hold',
  keypad: 'keypad',
  transfer: 'transfer',
  reply: 'reply',
} as const

type ControlId = (typeof CONTROL_ID)[keyof typeof CONTROL_ID]

const CALL_CONTROLS: {
  id: ControlId
  Icon: IconComponent
  labelKey: string
  disabled?: boolean
}[] = [
  { id: CONTROL_ID.mute, Icon: MicIcon, labelKey: 'massAction.controls.mute' },
  {
    id: CONTROL_ID.speaker,
    Icon: SpeakerIcon,
    labelKey: 'massAction.controls.speaker',
  },
  {
    id: CONTROL_ID.hold,
    Icon: PauseIcon,
    labelKey: 'massAction.controls.hold',
  },
  {
    id: CONTROL_ID.keypad,
    Icon: KeypadIcon,
    labelKey: 'massAction.controls.keypad',
  },
  {
    id: CONTROL_ID.transfer,
    Icon: TransferIcon,
    labelKey: 'massAction.controls.transfer',
    disabled: true,
  },
  {
    id: CONTROL_ID.reply,
    Icon: MessageIcon,
    labelKey: 'massAction.controls.reply',
  },
]

const ACTIVE_CIRCLE: Partial<Record<ControlId, string>> = {
  [CONTROL_ID.mute]: 'border-gray-900 bg-gray-900 text-white hover:bg-gray-900',
  [CONTROL_ID.speaker]:
    'border-blue-600 bg-blue-600 text-paper hover:bg-blue-600',
  [CONTROL_ID.hold]:
    'border-amber-500 bg-amber-500 text-paper hover:bg-amber-500',
  [CONTROL_ID.keypad]:
    'border-blue-600 bg-blue-600 text-paper hover:bg-blue-600',
}

const ACTIVE_LABEL: Partial<Record<ControlId, string>> = {
  [CONTROL_ID.mute]: 'text-gray-900',
  [CONTROL_ID.speaker]: 'text-blue-600',
  [CONTROL_ID.hold]: 'text-amber-500',
  [CONTROL_ID.keypad]: 'text-blue-600',
}

// One control in the row: a round icon button with a label underneath. Shared
// by the on-call controls and the incoming controls so both rows read
// identically (size, spacing, active/disabled treatment).
function ControlButton({
  Icon,
  label,
  active,
  activeCircle,
  activeLabel,
  disabled,
  onClick,
}: {
  Icon: IconComponent
  label: string
  active?: boolean
  activeCircle?: string
  activeLabel?: string
  disabled?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-12 cursor-pointer flex-col items-center gap-2 select-none',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      <span
        className={cn(
          'flex size-12 items-center justify-center rounded-full border border-warm-100 bg-gray-100 text-gray-500 transition-colors',
          !disabled && 'hover:bg-warm-50',
          active && activeCircle,
        )}
      >
        <Icon className="size-5 pointer-events-none" />
      </span>
      <span className={cn('text-xs text-gray-500', active && activeLabel)}>
        {label}
      </span>
    </button>
  )
}

export interface ControlState {
  muted: boolean
  held: boolean
  speakerOn: boolean
  onToggleMute: () => void
  onToggleHold: () => void
  onToggleSpeaker: () => void
}

export function CallControls({
  muted,
  held,
  speakerOn,
  onToggleMute,
  onToggleHold,
  onToggleSpeaker,
  onReply,
}: ControlState & { onReply: () => void }) {
  const { t } = useTranslation()
  const keypadActive = usePhonePanelViewStore((s) => s.keypadOpen)
  // Speaker routing only makes sense on mobile (loudspeaker vs earpiece); on
  // desktop the output is already the speakers, so hide the button there. On
  // mobile, drop the (placeholder) Transfer instead — six circles don't fit
  // the pill's width, so both breakpoints show five.
  const isMobile = !useMediaQuery('(min-width: 768px)')
  const controls = CALL_CONTROLS.filter((c) =>
    isMobile ? c.id !== CONTROL_ID.transfer : c.id !== CONTROL_ID.speaker,
  )
  const isActive = (id: ControlId) =>
    id === CONTROL_ID.mute
      ? muted
      : id === CONTROL_ID.speaker
        ? speakerOn
        : id === CONTROL_ID.hold
          ? held
          : id === CONTROL_ID.keypad
            ? keypadActive
            : false
  const toggle = (id: ControlId) => {
    if (id === CONTROL_ID.mute) onToggleMute()
    if (id === CONTROL_ID.speaker) onToggleSpeaker()
    if (id === CONTROL_ID.hold) onToggleHold()
    if (id === CONTROL_ID.keypad) openPhoneKeypad()
    if (id === CONTROL_ID.reply) onReply()
  }
  return (
    <div className="flex items-start justify-between">
      {controls.map(({ id, Icon, labelKey, disabled }) => (
        <ControlButton
          key={id}
          Icon={Icon}
          label={t(labelKey)}
          active={isActive(id)}
          activeCircle={ACTIVE_CIRCLE[id]}
          activeLabel={ACTIVE_LABEL[id]}
          disabled={disabled}
          onClick={() => toggle(id)}
        />
      ))}
    </div>
  )
}

/** Shown while an incoming call rings on top of a call the agent is already
 *  on — a regular call or the power dialer's customer leg. Accepting hangs
 *  that call up (and pauses the dialer until this call ends), so the agent
 *  must see the cost before hitting the green button. */
function AnswerWarning() {
  const { t } = useTranslation()
  const cost = useAnswerCost()
  if (!cost) return null
  return (
    <p className="rounded-md bg-amber-100 px-2.5 py-1.5 text-xs leading-5 text-amber-700">
      {cost === ANSWER_COST.powerdial
        ? t('massAction.call.answerHangsPowerdial')
        : t('massAction.call.answerHangsCurrent')}
    </p>
  )
}

// The incoming-call controls row. Silence is live (it mutes the ringtone —
// moved down here off the header action row). Speaker and Transfer are
// placeholders for now, shown disabled to match the design. Reply collapses the
// pill and opens the caller's conversation in the phone panel.
function IncomingControls({
  ringMuted,
  onToggleSilence,
  onReply,
}: {
  ringMuted: boolean
  onToggleSilence: () => void
  onReply: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start justify-between">
      <ControlButton
        Icon={ringMuted ? BellOffIcon : BellRingIcon}
        label={t('massAction.controls.silence')}
        active={ringMuted}
        activeCircle="border-gray-900 bg-gray-900 text-white hover:bg-gray-900"
        activeLabel="text-gray-900"
        onClick={onToggleSilence}
      />
      <ControlButton
        Icon={SpeakerIcon}
        label={t('massAction.controls.speaker')}
        disabled
      />
      <ControlButton
        Icon={TransferIcon}
        label={t('massAction.controls.transfer')}
        disabled
      />
      <ControlButton
        Icon={MessageIcon}
        label={t('massAction.controls.reply')}
        onClick={onReply}
      />
    </div>
  )
}

/** The "Mainline" chip paired with the org's friendly line name, sitting atop
 *  the dropdown body whenever the caller is on one of the org's shared lines. */
function MainlineRow({ mainline }: { mainline: CallMainline }) {
  return (
    <div className="flex items-center gap-2.75">
      <MainlineBadge labelled />
      {mainline.label ? (
        <span className="text-xs font-medium text-gray-500">
          {mainline.label}
        </span>
      ) : null}
    </div>
  )
}

interface CallPillBodyProps extends ControlState {
  phase: CallPhase
  contactName: string
  matches: CallMatch[]
  mainline: CallMainline | null
  loading?: boolean
  onOpened: () => void
  ringMuted: boolean
  onToggleSilence: () => void
  onReply: () => void
}

export function CallPillBody({
  phase,
  contactName,
  matches,
  mainline,
  loading,
  onOpened,
  ringMuted,
  onToggleSilence,
  onReply,
  ...controls
}: CallPillBodyProps) {
  return (
    <>
      {phase === CALL_PHASE.onCall ? (
        <CallControls {...controls} onReply={onReply} />
      ) : null}
      {phase === CALL_PHASE.onCall || phase === CALL_PHASE.ended ? (
        <PillTranscript
          speaker={contactName}
          onCall={phase === CALL_PHASE.onCall}
        />
      ) : null}
      {mainline ? <MainlineRow mainline={mainline} /> : null}
      {phase === CALL_PHASE.incoming ? (
        <>
          <AnswerWarning />
          <IncomingControls
            ringMuted={ringMuted}
            onToggleSilence={onToggleSilence}
            onReply={onReply}
          />
        </>
      ) : null}
      <MatchList matches={matches} loading={loading} onOpened={onOpened} />
    </>
  )
}
